import fabric from '@/lib/fabric';

export type FrameShape = 'rect' | 'roundedRect' | 'circle';

export interface FrameMeta {
  role: 'frame';
  frameShape: FrameShape;
}

type FramedObject = fabric.Object & {
  role?: string;
  frameShape?: FrameShape;
  name?: string;
  elementId?: string;
};

export function isFrameObject(obj: fabric.Object | null | undefined): boolean {
  return Boolean(obj && (obj as FramedObject).role === 'frame');
}

export function createFrameObject(
  shape: FrameShape,
  opts: {
    left: number;
    top: number;
    width: number;
    height: number;
    selectable?: boolean;
    evented?: boolean;
  }
): fabric.Object {
  const common = {
    left: opts.left,
    top: opts.top,
    fill: '#e8eef4',
    stroke: '#7b8aa0',
    strokeWidth: 2,
    strokeDashArray: [6, 4],
    opacity: 1,
    originX: 'left' as const,
    originY: 'top' as const,
    selectable: opts.selectable ?? true,
    evented: opts.evented ?? true,
  };

  let obj: fabric.Object;
  if (shape === 'circle') {
    obj = new fabric.Ellipse({
      ...common,
      rx: Math.max(1, opts.width / 2),
      ry: Math.max(1, opts.height / 2),
    });
  } else if (shape === 'roundedRect') {
    obj = new fabric.Rect({
      ...common,
      width: Math.max(1, opts.width),
      height: Math.max(1, opts.height),
      rx: 16,
      ry: 16,
    });
  } else {
    obj = new fabric.Rect({
      ...common,
      width: Math.max(1, opts.width),
      height: Math.max(1, opts.height),
    });
  }

  const meta = obj as FramedObject;
  meta.role = 'frame';
  meta.frameShape = shape;
  meta.name = 'Frame';
  return obj;
}

function cloneAsClipPath(frame: fabric.Object): fabric.Object {
  const shape = (frame as FramedObject).frameShape || 'rect';
  const bound = frame.getBoundingRect(true);
  let clip: fabric.Object;

  if (shape === 'circle' || frame.type === 'ellipse' || frame.type === 'circle') {
    clip = new fabric.Ellipse({
      left: bound.left + bound.width / 2,
      top: bound.top + bound.height / 2,
      originX: 'center',
      originY: 'center',
      rx: bound.width / 2,
      ry: bound.height / 2,
    });
  } else {
    const rx = shape === 'roundedRect' || (frame as fabric.Rect).rx ? 16 : 0;
    clip = new fabric.Rect({
      left: bound.left,
      top: bound.top,
      originX: 'left',
      originY: 'top',
      width: bound.width,
      height: bound.height,
      rx,
      ry: rx,
    });
  }

  // fabric v5 absolute clip coordinates
  (clip as fabric.Object & { absolutePositioned?: boolean }).absolutePositioned = true;

  return clip;
}

/**
 * Place an image into a frame using an absolute clipPath, then remove the placeholder.
 */
export function fillFrameWithImage(
  canvas: fabric.Canvas,
  frame: fabric.Object,
  image: fabric.Image
): fabric.Image {
  const bound = frame.getBoundingRect(true);
  const iw = image.width || 1;
  const ih = image.height || 1;
  const cover = Math.max(bound.width / iw, bound.height / ih);

  image.set({
    left: bound.left + bound.width / 2,
    top: bound.top + bound.height / 2,
    originX: 'center',
    originY: 'center',
    scaleX: cover,
    scaleY: cover,
    selectable: true,
    evented: true,
    clipPath: cloneAsClipPath(frame),
  });

  const meta = image as FramedObject & { name?: string };
  meta.role = 'framed-image';
  meta.frameShape = (frame as FramedObject).frameShape;
  meta.name = meta.name || 'Framed image';

  canvas.remove(frame);
  if (!canvas.getObjects().includes(image)) {
    canvas.add(image);
  }
  canvas.setActiveObject(image);
  image.setCoords();
  canvas.requestRenderAll();
  return image;
}

/**
 * Use the top object as a clipping mask for the object below (image or shape).
 * Expects exactly two selected objects; mask is the uppermost.
 */
export function applyClippingMask(canvas: fabric.Canvas): boolean {
  const active = canvas.getActiveObject();
  if (!active || active.type !== 'activeSelection') return false;

  const sel = active as fabric.ActiveSelection;
  const members = sel.getObjects();
  if (members.length !== 2) return false;

  // Higher index = above in Fabric stacking within selection order is unreliable;
  // use canvas object index.
  const ordered = members
    .slice()
    .sort((a, b) => canvas.getObjects().indexOf(a) - canvas.getObjects().indexOf(b));
  const target = ordered[0];
  const mask = ordered[1];

  canvas.discardActiveObject();

  const bound = mask.getBoundingRect(true);
  let clip: fabric.Object;

  if (mask.type === 'ellipse' || mask.type === 'circle') {
    clip = new fabric.Ellipse({
      left: bound.left + bound.width / 2,
      top: bound.top + bound.height / 2,
      originX: 'center',
      originY: 'center',
      rx: bound.width / 2,
      ry: bound.height / 2,
    });
  } else if (mask.type === 'triangle') {
    clip = new fabric.Triangle({
      left: bound.left,
      top: bound.top,
      width: bound.width,
      height: bound.height,
    });
  } else {
    clip = new fabric.Rect({
      left: bound.left,
      top: bound.top,
      width: bound.width,
      height: bound.height,
      rx: (mask as fabric.Rect).rx || 0,
      ry: (mask as fabric.Rect).ry || 0,
    });
  }
  (clip as fabric.Object & { absolutePositioned?: boolean }).absolutePositioned = true;

  target.set({ clipPath: clip });
  canvas.remove(mask);
  canvas.setActiveObject(target);
  target.setCoords();
  canvas.requestRenderAll();
  return true;
}

export function clearClipPath(canvas: fabric.Canvas): boolean {
  const obj = canvas.getActiveObject();
  if (!obj || !obj.clipPath) return false;
  obj.set({ clipPath: undefined });
  obj.setCoords();
  canvas.requestRenderAll();
  return true;
}
